import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LangContext";
import ScreenModal from "../components/ScreenModal";
import "./LandingPage.css";

const productMeta = [
  { icon: "badge", iconBg: "bg-blue-500/10", iconColor: "text-blue-500", blurColor: "bg-blue-500/[0.08]", screenSrc: "/screen1", frameType: "mobile" as const, reversed: false, bgClass: "bg-white" },
  { icon: "dashboard", iconBg: "bg-indigo-500/10", iconColor: "text-indigo-500", blurColor: "bg-indigo-500/[0.08]", screenSrc: "/screen2", frameType: "desktop" as const, reversed: true, bgClass: "bg-slate-50/70" },
  { icon: "task_alt", iconBg: "bg-emerald-500/10", iconColor: "text-emerald-500", blurColor: "bg-emerald-500/[0.08]", screenSrc: "/screen3", frameType: "mobile" as const, reversed: false, bgClass: "bg-white" },
  { icon: "receipt_long", iconBg: "bg-amber-500/10", iconColor: "text-amber-500", blurColor: "bg-amber-500/[0.08]", screenSrc: "/screen4", frameType: "desktop" as const, reversed: true, bgClass: "bg-slate-50/70" },
  { icon: "paid", iconBg: "bg-green-500/10", iconColor: "text-green-500", blurColor: "bg-green-500/[0.08]", screenSrc: "/screen5", frameType: "mobile" as const, reversed: false, bgClass: "bg-white" },
];

const whyIcons = ["speed", "sync", "bar_chart"];
const whyColors = ["text-red-500", "text-purple-500", "text-amber-500"];
const whyBgs = ["bg-red-500/10", "bg-purple-500/10", "bg-amber-500/10"];

const LandingPage: React.FC = () => {
  const { lang, t, setLang } = useLang();
  const [modalSrc, setModalSrc] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [openAccordion, setOpenAccordion] = useState<number | null>(null);
  const frameRefs = useRef<(HTMLDivElement | null)[]>([]);

  const scaleFrames = useCallback(() => {
    frameRefs.current.forEach((wrapper) => {
      if (!wrapper) return;
      const iframe = wrapper.querySelector("iframe");
      if (!iframe) return;
      const nativeW = parseInt(getComputedStyle(iframe).width);
      const containerW = wrapper.offsetWidth;
      const s = containerW / nativeW;
      wrapper.style.setProperty("--frame-scale", String(s));
    });
  }, []);

  useEffect(() => {
    scaleFrames();
    window.addEventListener("resize", scaleFrames);
    return () => window.removeEventListener("resize", scaleFrames);
  }, [scaleFrames]);

  const openModal = (src: string) => { setModalSrc(`${src}?lang=${lang}`); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setTimeout(() => setModalSrc(""), 300); };

  return (
    <div className="bg-white text-slate-800 font-display">
      {/* HEADER */}
      <nav className="fixed top-0 w-full z-50 glass-nav border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-6 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-10">
            <Link className="flex items-center gap-2 group" to="/">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center text-white font-bold text-lg group-hover:scale-105 transition-transform">W</div>
              <span className="text-xl font-bold tracking-tight text-primary">Worklly</span>
            </Link>
            <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-500">
              <a className="hover:text-primary transition-colors" href="#products">{t.nav.products}</a>
              <a className="hover:text-primary transition-colors" href="#features">{t.nav.features}</a>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative group">
              <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-primary/5 transition-colors text-sm font-medium text-slate-500">
                <span className="material-icons text-[18px]">language</span><span>{t.nav.langLabel}</span><span className="material-icons text-[14px]">expand_more</span>
              </button>
              <div className="absolute right-0 top-full pt-2 w-44 hidden group-hover:block z-50"><div className="bg-white rounded-xl shadow-xl border border-slate-100 py-2">
                <span
                  className={`flex items-center gap-3 px-4 py-2 hover:bg-primary/5 text-sm font-medium cursor-pointer ${lang === "fi" ? "text-primary" : "text-slate-500"}`}
                  onClick={() => setLang("fi")}
                >
                  <span className={`w-2 h-2 rounded-full ${lang === "fi" ? "bg-primary" : "border border-slate-300"}`} /> Suomi
                </span>
                <span
                  className={`flex items-center gap-3 px-4 py-2 hover:bg-primary/5 text-sm font-medium cursor-pointer ${lang === "en" ? "text-primary" : "text-slate-500"}`}
                  onClick={() => setLang("en")}
                >
                  <span className={`w-2 h-2 rounded-full ${lang === "en" ? "bg-primary" : "border border-slate-300"}`} /> English
                </span>
              </div></div>
            </div>
            <div className="h-5 w-px bg-slate-200 mx-1" />
            <Link to="/signup" className="bg-primary hover:bg-primary/90 text-white px-5 py-2.5 rounded-full text-sm font-bold shadow-lg shadow-primary/20 transition-all active:scale-95">{t.nav.joinWaitlist}</Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="pt-36 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <a href="#products" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/5 border border-primary/15 text-primary text-xs font-bold uppercase tracking-wider mb-8 hover:bg-primary/10 transition-colors">
            <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-primary" /></span>
            {t.hero.badge} &rarr;
          </a>
          <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-6 leading-[1.08]">
            {t.hero.heading}<span className="text-primary">{t.hero.headingAccent}</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-500 max-w-2xl mx-auto mb-10 leading-relaxed">
            {t.hero.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup" className="bg-primary hover:bg-primary/90 text-white px-10 py-4 rounded-full text-lg font-bold shadow-xl shadow-primary/25 transition-all active:scale-95 flex items-center justify-center gap-2">
              {t.hero.cta} <span className="material-icons text-[20px]">arrow_forward</span>
            </Link>
            <a href="#overview" className="bg-white border border-slate-200 hover:border-primary/40 text-slate-600 hover:text-primary px-10 py-4 rounded-full text-lg font-bold transition-all inline-flex items-center justify-center">{t.hero.learnMore}</a>
          </div>
        </div>
      </section>

      {/* OVERVIEW BANNER */}
      <section id="overview" className="py-16 px-6 bg-slate-50 border-y border-slate-200/60">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-8">{t.overview.heading}</h2>
          <ul className="flex flex-col sm:flex-row justify-center gap-6 sm:gap-10 mb-8">
            {t.overview.bullets.map((item, i) => (
              <li key={i} className="flex items-center gap-2 text-slate-600 text-sm sm:text-base font-medium">
                <span className="material-icons text-primary text-[20px] flex-shrink-0">check_circle</span>
                {item}
              </li>
            ))}
          </ul>
          <a href="#all-features" className="inline-flex items-center gap-2 text-primary font-bold text-lg hover:gap-3 transition-all">
            {t.overview.exploreAll} <span className="material-icons text-[20px]">arrow_forward</span>
          </a>
        </div>
      </section>

      {/* PRODUCTS */}
      <section id="products" className="pb-8">
        {productMeta.map((p, i) => {
          const pt = t.products[i];
          return (
            <div className={`py-20 px-6 ${p.bgClass}`} key={i}>
              <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                {p.reversed ? (
                  <>
                    <div className="relative order-2 lg:order-1">
                      <div className={`absolute -inset-4 ${p.blurColor} rounded-3xl blur-2xl`} />
                      <div
                        className={`relative rounded-2xl shadow-2xl shadow-slate-900/10 border border-slate-200/60 frame-wrapper ${p.frameType}`}
                        ref={(el) => { frameRefs.current[i] = el; }}
                        onClick={() => openModal(p.screenSrc)}
                      >
                        <iframe src={`${p.screenSrc}?lang=${lang}`} scrolling="no" loading="lazy" title={pt.title} />
                        <div className="frame-expand"><span className="material-icons" style={{ fontSize: 28 }}>fullscreen</span></div>
                      </div>
                    </div>
                    <div className="order-1 lg:order-2">
                      <div className={`w-14 h-14 rounded-2xl ${p.iconBg} flex items-center justify-center mb-6`}>
                        <span className={`material-icons ${p.iconColor} text-[28px]`}>{p.icon}</span>
                      </div>
                      <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 leading-tight">{pt.title}</h2>
                      <p className="text-slate-500 text-lg leading-relaxed mb-6">{pt.desc}</p>
                      <ul className="space-y-3 mb-8">
                        {pt.features.map((f, j) => (<li key={j} className="flex items-center gap-3 text-slate-600"><span className="material-icons text-primary text-[20px]">check_circle</span> {f}</li>))}
                      </ul>
                      <a href="#all-features" className="inline-flex items-center gap-2 text-primary font-bold hover:gap-3 transition-all">{t.hero.learnMore} <span className="material-icons text-[18px]">arrow_forward</span></a>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <div className={`w-14 h-14 rounded-2xl ${p.iconBg} flex items-center justify-center mb-6`}>
                        <span className={`material-icons ${p.iconColor} text-[28px]`}>{p.icon}</span>
                      </div>
                      <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 leading-tight">{pt.title}</h2>
                      <p className="text-slate-500 text-lg leading-relaxed mb-6">{pt.desc}</p>
                      <ul className="space-y-3 mb-8">
                        {pt.features.map((f, j) => (<li key={j} className="flex items-center gap-3 text-slate-600"><span className="material-icons text-primary text-[20px]">check_circle</span> {f}</li>))}
                      </ul>
                      <a href="#all-features" className="inline-flex items-center gap-2 text-primary font-bold hover:gap-3 transition-all">{t.hero.learnMore} <span className="material-icons text-[18px]">arrow_forward</span></a>
                    </div>
                    <div className="relative">
                      <div className={`absolute -inset-4 ${p.blurColor} rounded-3xl blur-2xl`} />
                      <div
                        className={`relative rounded-2xl shadow-2xl shadow-slate-900/10 border border-slate-200/60 frame-wrapper ${p.frameType}`}
                        ref={(el) => { frameRefs.current[i] = el; }}
                        onClick={() => openModal(p.screenSrc)}
                      >
                        <iframe src={`${p.screenSrc}?lang=${lang}`} scrolling="no" loading="lazy" title={pt.title} />
                        <div className="frame-expand"><span className="material-icons" style={{ fontSize: 28 }}>fullscreen</span></div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </section>

      {/* ALL FEATURES ACCORDION */}
      <section id="all-features" className="py-24 px-6 bg-slate-50/70">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">{t.accordion.heading}</h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">{t.accordion.subtitle}</p>
          </div>
          <div className="accordion-list">
            {t.accordion.items.map((feat, i) => {
              const isOpen = openAccordion === i;
              return (
                <div className="accordion-item" key={i}>
                  <button
                    className="accordion-trigger"
                    onClick={() => setOpenAccordion(isOpen ? null : i)}
                    aria-expanded={isOpen}
                  >
                    <h3 className="text-lg font-bold text-slate-800">{feat.title}</h3>
                    <span className={`material-icons accordion-icon${isOpen ? " open" : ""}`}>add</span>
                  </button>
                  <div className={`accordion-content${isOpen ? " open" : ""}`}>
                    <p className="text-slate-500 leading-relaxed pb-5 pr-8">{feat.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY WORKLLY */}
      <section id="features" className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">{t.why.heading}</h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">{t.why.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.why.cards.map((card, i) => (
              <div key={i} className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg hover:shadow-primary/5 transition-all">
                <div className={`w-12 h-12 rounded-xl ${whyBgs[i]} flex items-center justify-center mb-5`}><span className={`material-icons ${whyColors[i]} text-[24px]`}>{whyIcons[i]}</span></div>
                <h3 className="text-xl font-bold mb-3">{card.title}</h3>
                <p className="text-slate-500 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t.cta.heading}</h2>
          <p className="text-lg text-white/70 mb-10">{t.cta.subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup" className="bg-white text-primary px-10 py-4 rounded-full text-lg font-bold shadow-xl hover:shadow-2xl transition-all active:scale-95">{t.cta.primary}</Link>
            <a href="#all-features" className="border-2 border-white/30 text-white px-10 py-4 rounded-full text-lg font-bold hover:bg-white/10 transition-all inline-flex items-center justify-center">{t.cta.secondary}</a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-slate-900 text-white pt-16 pb-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10 mb-14">
            <div className="col-span-2 lg:col-span-1">
              <Link className="flex items-center gap-2 mb-5" to="/">
                <div className="w-7 h-7 bg-primary rounded-lg flex items-center justify-center text-white font-bold text-sm">W</div>
                <span className="text-lg font-bold tracking-tight">Worklly</span>
              </Link>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">{t.footer.tagline}</p>
            </div>
            <div>
              <h4 className="font-bold text-white mb-5 text-sm uppercase tracking-wider">{t.footer.productsTitle}</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                {t.footer.productLinks.map((link, i) => (
                  <li key={i}><a className="hover:text-white transition-colors" href="#">{link}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white mb-5 text-sm uppercase tracking-wider">{t.footer.supportTitle}</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                {t.footer.supportLinks.map((link, i) => (
                  <li key={i}><a className="hover:text-white transition-colors" href="#">{link}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white mb-5 text-sm uppercase tracking-wider">{t.footer.legalTitle}</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                {t.footer.legalLinks.map((link, i) => (
                  <li key={i}><a className="hover:text-white transition-colors" href="#">{link}</a></li>
                ))}
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500">
            <p>&copy; {t.footer.copyright}</p>
            <div className="flex items-center gap-2"><span className="material-icons text-[14px]">location_on</span><span>{t.footer.location}</span></div>
          </div>
        </div>
      </footer>

      <ScreenModal src={modalSrc} isOpen={modalOpen} onClose={closeModal} />
    </div>
  );
};

export default LandingPage;
